/**
 * @fileoverview Spheres, cylinders (solid or dashed), cones and arrows: the handles BMaps gets
 * back synchronously, and the pooled CGO objects that draw them.
 *
 * Handles. `drawSphere()` & co. return a plain object at once. The app keeps it, hangs its own
 * properties on it (`description`, `onClick`, `object`, anything else), passes it back to
 * `remove*()`, and gets THE SAME object in `publishMouseEvent({shape})` - display_mgr calls
 * `shape.onClick(shape, event)`, UIManager reads `shape.description` for the tooltip.
 *
 * Pools. Never one PyMOL object per shape: shapes are pooled into CGO objects (`_bm_shp<n>`),
 * one per primitive kind (spheres / cylinders / cones - `bridge.cgo_pool` of payload 0.1.0
 * takes one kind per call) and opacity. A pool is rebuilt as a whole (one `cgo_pool` call, 1 000
 * spheres = 2-5 ms) when a shape in it is added or removed, so the shapes created between two
 * flushes form pools of their own: removing the H-bonds does not rebuild the 1 000 water
 * halos. Small batches join a small pool of the same kind, which bounds the object count.
 *
 * Picking. Every primitive carries `PICK_COLOR <pool index>`; PyMOL reports a click on it as
 * `{type: 'object:cgo', object: <pool>, index: <pool index> + 1}` (pymol-web wrapper.md 8, and
 * re-verified here). Transparent CGO is not drawn in PyMOL's pick pass - a click goes to what
 * is behind it (VERIFIED on payload 0.1.0), which is what 3Dmol does for the shapes BMaps
 * makes transparent (halos: description only, not clickable). For the rare clickable
 * transparent shape, and for hover (PyMOL has no hover), `hitTest()` works on the shadow
 * geometry in JavaScript.
 */

import { guarded } from './PyMOLGuard';
import {
    colorToInt, ObjectPrefix, ObjectKinds, ShapeDefaults,
} from './PyMOLConstants';
import { pointSegment2D, sameView } from './PyMOLProjection';

/** Shapes created in one flush that stay below this join an existing small pool. */
const SmallBatch = 64;
/** ... if that pool holds fewer than this. */
const SmallPool = 512;
/** Minimum hit radius on screen (CSS px), so a thin cylinder can still be hovered. */
const MinHitPixels = 3;

const PrimitiveKinds = ['spheres', 'cylinders', 'cones'];
const FloatsPerPrimitive = { spheres: 7, cylinders: 13, cones: 14 };

function toPoint(p) {
    if (!p) return [0, 0, 0];
    if (Array.isArray(p) || ArrayBuffer.isView(p)) {
        return [Number(p[0]) || 0, Number(p[1]) || 0, Number(p[2]) || 0];
    }
    return [Number(p.x) || 0, Number(p.y) || 0, Number(p.z) || 0];
}

function rgbOf(color) {
    let value = colorToInt(color);
    if (value == null) value = 0xffffff;
    /* eslint-disable no-bitwise */
    return [((value >> 16) & 0xff) / 255, ((value >> 8) & 0xff) / 255, (value & 0xff) / 255];
    /* eslint-enable no-bitwise */
}

const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const addScaled = (a, v, s) => [a[0] + v[0] * s, a[1] + v[1] * s, a[2] + v[2] * s];
const length3 = (v) => Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);

/** 3Dmol's opacity rule (finishShape): undefined -> 1.0, null -> not set (opaque). */
export function normalizedOpacity(opacity) {
    if (opacity == null || !Number.isFinite(Number(opacity))) return 1;
    return Math.max(0, Math.min(1, Number(opacity)));
}

/** Pool key part for an opacity: opaque shapes are pickable impostors, the rest share a level. */
function opacityKey(opacity) {
    return opacity >= 0.995 ? 'o' : `t${Math.round(opacity * 100)}`;
}

/**
 * Geometry of a shape: primitives per kind (float rows for cgo_pool) and hit-test parts.
 * @returns {{prims: Object<string, number[][]>, parts: object[]}}
 */
export function shapeGeometry(kind, spec) {
    const prims = { spheres: [], cylinders: [], cones: [] };
    const parts = [];
    const [r, g, b] = rgbOf(spec.color);
    const cylinder = (p, q, radius) => {
        prims.cylinders.push([...p, ...q, radius, r, g, b, r, g, b]);
    };
    const cone = (p, q, radius1, radius2) => {
        prims.cones.push([...p, ...q, radius1, radius2, r, g, b, r, g, b]);
    };
    if (kind === 'sphere') {
        const c = toPoint(spec.center);
        const radius = Number(spec.radius) || ShapeDefaults.sphereRadius;
        prims.spheres.push([...c, radius, r, g, b]);
        parts.push({ type: 'sphere', c, r: radius });
        return { prims, parts };
    }
    const start = toPoint(spec.start);
    const end = toPoint(spec.end);
    if (kind === 'cylinder' || kind === 'line') {
        const radius = kind === 'line' ? ShapeDefaults.lineRadius
            : (Number(spec.radius) || ShapeDefaults.cylinderRadius);
        if (spec.dashed) {
            // GLShape.addDashedCylinder: 0.25 A dashes, 0.25 A gaps, from the start
            const axis = sub(end, start);
            const len = length3(axis);
            const period = ShapeDefaults.dashLength + ShapeDefaults.gapLength;
            for (let s = 0; s < len; s += period) {
                const e = Math.min(len, s + ShapeDefaults.dashLength);
                cylinder(addScaled(start, axis, s / len), addScaled(start, axis, e / len), radius);
            }
        } else {
            cylinder(start, end, radius);
        }
        parts.push({
            type: 'segment', a: start, b: end, r0: radius, r1: radius,
        });
        return { prims, parts };
    }
    if (kind === 'cone') {
        // 3Dmol addCone: base (radius) at start, apex at end
        const radius = Number(spec.radius) || ShapeDefaults.cylinderRadius;
        cone(start, end, radius, 0);
        parts.push({
            type: 'segment', a: start, b: end, r0: radius, r1: 0,
        });
        return { prims, parts };
    }
    if (kind === 'arrow') {
        // GLShape.drawArrow: shaft from start to start + mid * (end - start), then a cone of
        // radius radius * radiusRatio to the end
        const radius = Number(spec.radius) || ShapeDefaults.arrowRadius;
        const ratio = Number(spec.radiusRatio) || ShapeDefaults.arrowRadiusRatio;
        const mid = addScaled(start, sub(end, start), ShapeDefaults.arrowMid);
        cylinder(start, mid, radius);
        cone(mid, end, radius * ratio, 0);
        parts.push({
            type: 'segment', a: start, b: mid, r0: radius, r1: radius,
        }, {
            type: 'segment', a: mid, b: end, r0: radius * ratio, r1: 0,
        });
        return { prims, parts };
    }
    return { prims, parts };
}

function newStats() {
    return {
        deleted: 0, built: 0, primitives: 0, errors: 0,
    };
}

export class PyMOLShapes {
    constructor() {
        /** @type {Map<object, object>} handle -> record */
        this.records = new Map();
        /** Records drawn since the last flush, not in a pool yet. */
        this.pending = new Set();
        /** @type {Map<string, object>} pool name -> pool */
        this.pools = new Map();
        this.poolSeq = 0;
        this.nextHandleId = 1;
        this.lastStats = newStats();
        this.hitCache = null;
    }

    get size() { return this.records.size; }

    has(handle) { return !!handle && this.records.has(handle); }

    /**
     * Create a handle. Synchronous; PyMOL sees it at the next flush.
     * @param {string} kind sphere | cylinder | cone | arrow | line
     * @param {object} spec kind-specific geometry, kept on the handle as given
     * @param {{opacity?: number, description?: *, onClick?: function, object?: *}} [options]
     */
    add(kind, spec, {
        opacity=1.0, description, onClick, object,
    }={}) {
        const handle = { id: this.nextHandleId++, kind, ...spec };
        // As Visualizer3Dmol.finishShape(): the app reads these back on shapeMouse events.
        if (opacity != null) handle.opacity = opacity;
        if (description) handle.description = description;
        if (onClick) handle.onClick = onClick;
        if (object) handle.object = object;
        const alpha = normalizedOpacity(opacity);
        const { prims, parts } = shapeGeometry(kind, spec);
        const record = {
            handle, prims, parts, opacity: alpha, opacityKey: opacityKey(alpha), pools: new Set(),
        };
        this.records.set(handle, record);
        this.pending.add(record);
        this.hitCache = null;
        return handle;
    }

    remove(handle) {
        const record = handle && this.records.get(handle);
        if (!record) return false;
        this.records.delete(handle);
        this.pending.delete(record);
        for (const pool of record.pools) {
            pool.records.delete(record);
            pool.dirty = true;
        }
        record.pools.clear();
        this.hitCache = null;
        return true;
    }

    isPoolName(name) { return this.pools.has(name); }

    /** The handle a PyMOL pick names, or null (a removed shape, someone else's CGO). */
    handleForPick(objectName, index) {
        const pool = this.pools.get(objectName);
        if (!pool || !pool.pickTable || !Number.isInteger(index)) return null;
        const record = pool.pickTable[index - 1];
        return record && this.records.get(record.handle) === record ? record.handle : null;
    }

    // ------------------------------------------------------------------ flush

    newPool(kind, key, opacity) {
        this.poolSeq++;
        const pool = {
            name: `${ObjectPrefix}${ObjectKinds.shapes}${this.poolSeq}`,
            kind,
            key,
            opacity,
            records: new Set(),
            dirty: true,
            loaded: false,
            pickTable: [],
        };
        this.pools.set(pool.name, pool);
        return pool;
    }

    /** Put the shapes drawn since the last flush into pools. */
    assignPending() {
        /** @type {Map<string, object[]>} kind:opacityKey -> records */
        const batches = new Map();
        for (const record of this.pending) {
            for (const kind of PrimitiveKinds) {
                if (record.prims[kind].length === 0) continue;
                const key = `${kind}:${record.opacityKey}`;
                if (!batches.has(key)) batches.set(key, []);
                batches.get(key).push(record);
            }
        }
        this.pending.clear();
        for (const [key, records] of batches) {
            const [kind] = key.split(':');
            let pool = null;
            if (records.length < SmallBatch) {
                const candidates = [...this.pools.values()].filter(
                    (p) => p.key === key && p.records.size < SmallPool,
                );
                pool = candidates[candidates.length - 1] || null;
            }
            if (!pool) pool = this.newPool(kind, key, records[0].opacity);
            for (const record of records) {
                pool.records.add(record);
                record.pools.add(pool);
            }
            pool.dirty = true;
        }
    }

    /**
     * Push pools to PyMOL: delete the empty ones, rebuild the changed ones. Synchronous.
     * A failing pool is logged and the others still flush.
     */
    flush(viewer) {
        const stats = newStats();
        this.lastStats = stats;
        if (this.pending.size > 0) this.assignPending();
        for (const pool of [...this.pools.values()]) {
            if (!pool.dirty) continue;
            if (pool.records.size === 0) {
                if (pool.loaded) {
                    this.guard(stats, `delete ${pool.name}`, () => { viewer.delete(pool.name); stats.deleted++; });
                }
                this.pools.delete(pool.name);
                continue;
            }
            this.guard(stats, `build ${pool.name}`, () => this.buildPool(viewer, pool, stats));
        }
        return stats;
    }

    guard(stats, what, fn) {
        return guarded(stats, `shapes: ${what}`, fn);
    }

    buildPool(viewer, pool, stats) {
        // `dirty` stays set until PyMOL has the pool: a build that throws (the guard logs it)
        // is retried by the next flush instead of leaving the pool deleted and forgotten.
        const width = FloatsPerPrimitive[pool.kind];
        const rows = [];
        const pickIds = [];
        pool.pickTable = [];
        for (const record of pool.records) {
            const index = pool.pickTable.length;
            pool.pickTable.push(record);
            for (const row of record.prims[pool.kind]) {
                rows.push(row);
                pickIds.push(index);
            }
        }
        const floats = new Float32Array(rows.length * width);
        rows.forEach((row, i) => floats.set(row, i * width));
        if (pool.loaded) {
            viewer.delete(pool.name);
            pool.loaded = false;
        }
        viewer.bridge.cgo_pool(pool.name, pool.kind, floats, Int32Array.from(pickIds));
        pool.loaded = true;
        pool.dirty = false;
        if (pool.opacity < 0.995) viewer.cmd.set('cgo_transparency', 1 - pool.opacity, pool.name);
        stats.built++;
        stats.primitives += rows.length;
    }

    // ------------------------------------------------------------------ hit-testing

    /**
     * Front-most shape under (x, y) CSS pixels among those `accept(handle)` allows.
     * @param {{view: number[], width: number, height: number, project: function}} projection
     * @returns {{handle: object, front: number}|null} front = distance of the shape's surface
     *   from the camera (Angstrom)
     */
    hitTest(projection, x, y, accept) {
        if (!projection || this.records.size === 0) return null;
        const cache = this.projectedParts(projection);
        let best = null;
        for (const { record, screen } of cache) {
            if (!accept(record.handle)) continue;
            for (const part of screen) {
                const front = hitPart(part, x, y);
                if (front !== null && (!best || front < best.front)) {
                    best = { handle: record.handle, front };
                }
            }
        }
        return best;
    }

    /** Screen-space parts of every shape, cached per view and viewport. */
    projectedParts({
        view, width, height, project,
    }) {
        const key = [...view, width, height];
        if (this.hitCache && sameView(this.hitCache.key, key)) return this.hitCache.list;
        const list = [];
        for (const record of this.records.values()) {
            const screen = record.parts.map((part) => {
                if (part.type === 'sphere') {
                    return { type: 'sphere', p: project(...part.c), r: part.r };
                }
                return {
                    type: 'segment', p: project(...part.a), q: project(...part.b), r0: part.r0, r1: part.r1,
                };
            });
            list.push({ record, screen });
        }
        this.hitCache = { key, list };
        return list;
    }
}

/** @returns {number|null} front depth when (x, y) is on the projected part */
function hitPart(part, x, y) {
    if (part.type === 'sphere') {
        const { p, r } = part;
        if (p.depth <= 0) return null;
        const radiusPx = Math.max(MinHitPixels, r * p.scale);
        const dx = p.x - x;
        const dy = p.y - y;
        if (dx * dx + dy * dy > radiusPx * radiusPx) return null;
        return p.depth - r;
    }
    const {
        p, q, r0, r1,
    } = part;
    if (p.depth <= 0 || q.depth <= 0) return null;
    const { dist, t } = pointSegment2D(x, y, p.x, p.y, q.x, q.y);
    const r = r0 + (r1 - r0) * t;
    const scale = p.scale + (q.scale - p.scale) * t;
    if (dist > Math.max(MinHitPixels, r * scale)) return null;
    return p.depth + (q.depth - p.depth) * t - r;
}
